import { LoadingCircle, showToastMessage } from "@almafintech/react-components";
import * as Sentry from "@sentry/react";
import { FormikValues } from "formik";
import { AppDispatch } from "../../app/store";
import RiskProfileFeedback from "../../components/RiskProfileFeedback/RiskProfileFeedback";
import { submitQuestionnaire } from "../../connectors/interactiveBrokers";
import {
  placeholders,
  title,
} from "../../i18n/interactiveBrokers/investmentProfileStep";
import { onUpdateInteractiveBrokersData } from "../../reducers/interactiveBrokersReducer";
import styles from "../../styles/interactiveBrokers.module.scss";
import { BasicStep, Field, Step } from "../../types";
import { RiskProfileQuestionnaire } from "../../types/interactiveBrokers";
import { genericToastifyError } from "../../utils";
import { validationsForLanguage } from "../../validations";

const { loading, label, riskProfileContainer } = styles;

const investmentProfileStep = ({
  riskProfileQuestionnaire,
  language,
}: {
  riskProfileQuestionnaire?: RiskProfileQuestionnaire;
  language: "es" | "en";
}): Step => {
  const validations = validationsForLanguage(language);

  const getRiskProfileQuestionFieldName = (id: number) =>
    `riskProfileQuestion-${id}`;

  const fields: Field[] =
    riskProfileQuestionnaire?.questions.flatMap(
      ({ id, statement, options }) => [
        {
          name: `${getRiskProfileQuestionFieldName(id)}-label`,
          type: "label",
          label: "",
          hideLabel: true,
          text: <p className={label}>{statement}</p>,
          initialValue: "",
        },
        {
          name: getRiskProfileQuestionFieldName(id),
          type: "select",
          label: "",
          hideLabel: true,
          placeholder: placeholders[language].riskProfileQuestion,
          validation: validations.radioOrSingleSelectRequired(options),
          options,
          selectType: "single",
          initialValue: "",
          multilineSelect: true,
        },
      ]
    ) ?? [];

  const maxQuestions = 5;
  const fieldsPerQuestion = 2;
  const numberOfSubsteps = Math.ceil(
    (fields?.length ?? 0) / fieldsPerQuestion / maxQuestions
  );

  const questionsSubsteps: BasicStep[] =
    [...Array(numberOfSubsteps).keys()].map((index) => ({
      step: index + 1,
      name: `riskProfileQuestions${index + 1}`,
      fields: fields?.slice(
        index * fieldsPerQuestion * maxQuestions,
        (index + 1) * fieldsPerQuestion * maxQuestions
      ),
      onBack: async (_values, dispatch) => {
        dispatch?.(
          onUpdateInteractiveBrokersData({ riskProfileQuestionnaire: null })
        );
        return {
          backStep: 2,
          backSubStep: 1,
        };
      },
      onContinue: async (values: FormikValues, dispatch?: AppDispatch) => {
        const isLastQuestionsSubstep = index === numberOfSubsteps - 1;
        if (!isLastQuestionsSubstep || !riskProfileQuestionnaire) return;

        try {
          const answers =
            riskProfileQuestionnaire.questions.map(({ id }) => ({
              question: id,
              selected_answer: Number.parseInt(
                values[getRiskProfileQuestionFieldName(id)]
              ),
            })) ?? [];

          const {
            data: {
              id,
              calculated_risk_profile: { name, description },
            },
          } = await submitQuestionnaire({
            questionnaire: riskProfileQuestionnaire.id,
            answers,
          });

          dispatch?.(
            onUpdateInteractiveBrokersData({
              riskProfile: { id, name, description },
            })
          );
        } catch (error) {
          console.error("Error on step three questionaire:", error);
          Sentry.captureException(error);
          showToastMessage(genericToastifyError, {
            containerId: "formAlert",
            type: "error",
          });
        }
      },
    })) ?? [];

  const confirmRiskProfileSubStep: Step = {
    step: numberOfSubsteps + 1,
    name: "confirmRiskProfile",
    titleOfStep: title[language],
    hideFromSubStepper: true,
    containerClassName: riskProfileContainer,
    component: (dispatch, state) => (
      <RiskProfileFeedback
        returnSubstep={numberOfSubsteps}
        riskProfile={state?.interactiveBrokers.riskProfile ?? null}
        language={language}
        dispatch={dispatch}
      />
    ),
  };

  return {
    step: 3,
    name: title[language],
    titleOfStep: title[language],
    ...(riskProfileQuestionnaire
      ? {
          subSteps: [...questionsSubsteps, confirmRiskProfileSubStep],
        }
      : {
          component: () => (
            <div className={loading}>
              <LoadingCircle width="50" />
            </div>
          ),
          hideLayoutButtons: true,
        }),
  };
};

export default investmentProfileStep;
